(() => {
  const selector = '[data-carben-offers]';
  const imageDuration = 8000;

  function initialize(root) {
    if (root.carbenOffersController) return;
    const panels = [...root.querySelectorAll('[data-offer-panel]')];
    if (!panels.length) return;
    const choices = [...root.querySelectorAll('[data-offer-select]')];
    const toggle = root.querySelector('[data-offer-toggle]');
    const toggleLabel = root.querySelector('[data-offer-toggle-label]');
    const currentTitle = root.querySelector('[data-offer-current-title]');
    const position = root.querySelector('[data-offer-position]');
    const productLink = root.querySelector('[data-offer-product]');
    const productLabel = root.querySelector('[data-offer-product-label]');
    const notice = root.querySelector('[data-offer-status]');
    const playlist = root.querySelector('[data-offer-playlist]');
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const scriptedPauses = new WeakSet();
    const scriptedPlays = new WeakSet();
    const disposers = [];
    let index = 0;
    let visible = false;
    let userPaused = false;
    let manualPlayback = false;
    let imageTimer = null;
    let imageStarted = 0;
    let imageRemaining = imageDuration;
    let destroyed = false;
    const video = () => panels[index].querySelector('video');
    const permitted = () => !destroyed && visible && !document.hidden && !userPaused && (!motion.matches || manualPlayback);
    const listen = (target, name, handler, options) => {
      target.addEventListener(name, handler, options);
      disposers.push(() => target.removeEventListener(name, handler, options));
    };

    function pauseVideo(media) {
      if (!media || media.paused) return;
      scriptedPauses.add(media);
      media.pause();
    }

    function pauseImage() {
      if (imageTimer === null) return;
      window.clearTimeout(imageTimer);
      imageTimer = null;
      imageRemaining = Math.max(0, imageRemaining - (Date.now() - imageStarted));
    }

    function updateControls() {
      const media = video();
      const playing = media ? !media.paused && !media.ended : imageTimer !== null;
      toggle.setAttribute('aria-pressed', String(playing));
      toggleLabel.textContent = playing ? root.dataset.labelPause : root.dataset.labelPlay;
    }

    function synchronize() {
      if (destroyed) return;
      const media = video();
      if (!permitted()) {
        pauseImage();
        pauseVideo(media);
        updateControls();
        return;
      }
      if (media) {
        if (media.paused && !media.error && !scriptedPlays.has(media)) {
          media.preload = 'metadata';
          scriptedPlays.add(media);
          let attempt;
          try { attempt = media.play(); } catch {
            scriptedPlays.delete(media);
            userPaused = true;
            notice.textContent = root.dataset.labelBlocked;
            updateControls();
            return;
          }
          if (attempt && typeof attempt.then === 'function') {
            attempt.then(() => {
              if (destroyed || media !== video() || !permitted()) pauseVideo(media);
              if (!destroyed) updateControls();
            }).catch(() => {
              scriptedPlays.delete(media);
              if (destroyed || media !== video() || !permitted()) return;
              userPaused = true;
              notice.textContent = root.dataset.labelBlocked;
              updateControls();
            });
          }
        }
      } else if (panels.length > 1 && imageTimer === null) {
        imageStarted = Date.now();
        imageTimer = window.setTimeout(() => {
          imageTimer = null;
          select((index + 1) % panels.length, false);
        }, imageRemaining);
      }
      updateControls();
    }

    function select(nextIndex, manual) {
      if (destroyed || !Number.isInteger(nextIndex) || nextIndex < 0 || nextIndex >= panels.length) return;
      pauseImage();
      pauseVideo(video());
      index = nextIndex;
      imageRemaining = imageDuration;
      if (manual) {
        manualPlayback = true;
        userPaused = false;
      }
      const media = video();
      if (media) {
        media.muted = true;
        try { media.currentTime = 0; } catch { /* A source may not have metadata yet. */ }
      }
      panels.forEach((panel, panelIndex) => { panel.hidden = panelIndex !== index; });
      choices.forEach(choice => { choice.setAttribute('aria-pressed', String(Number(choice.dataset.offerSelect) === index)); });
      currentTitle.textContent = panels[index].dataset.offerTitle;
      position.textContent = root.dataset.labelPosition.replace('[number]', String(index + 1)).replace('[total]', String(panels.length));
      const destination = panels[index].dataset.offerLink;
      productLink.hidden = !destination;
      if (destination) productLink.setAttribute('href', destination);
      else productLink.removeAttribute('href');
      if (productLabel) productLabel.textContent = panels[index].dataset.offerCtaLabel;
      notice.textContent = '';
      synchronize();
    }

    panels.forEach(panel => {
      const media = panel.querySelector('video');
      if (!media) return;
      media.muted = true;
      media.defaultMuted = true;
      media.playsInline = true;
      media.loop = false;
      listen(media, 'ended', () => {
        if (media === video() && !userPaused) select((index + 1) % panels.length, false);
      });
      listen(media, 'play', () => {
        const scripted = scriptedPlays.has(media);
        scriptedPlays.delete(media);
        if (media !== video() || !visible || document.hidden) { pauseVideo(media); return; }
        if (!scripted) { manualPlayback = true; userPaused = false; }
        notice.textContent = '';
        updateControls();
      });
      listen(media, 'pause', () => {
        const scripted = scriptedPauses.has(media);
        scriptedPauses.delete(media);
        if (media !== video()) return;
        if (!scripted && !media.ended && visible && !document.hidden) userPaused = true;
        updateControls();
      });
      listen(media, 'error', () => {
        if (media !== video()) return;
        userPaused = true;
        notice.textContent = root.dataset.labelUnavailable;
        updateControls();
      });
    });
    choices.forEach(choice => listen(choice, 'click', () => select(Number(choice.dataset.offerSelect), true)));
    listen(toggle, 'click', () => {
      const media = video();
      const playing = media ? !media.paused && !media.ended : imageTimer !== null;
      userPaused = playing;
      if (!playing) { manualPlayback = true; notice.textContent = ''; }
      synchronize();
    });
    listen(document, 'visibilitychange', synchronize);
    const motionChanged = () => { manualPlayback = false; synchronize(); };
    if (motion.addEventListener) {
      listen(motion, 'change', motionChanged);
    } else {
      motion.addListener(motionChanged);
      disposers.push(() => motion.removeListener(motionChanged));
    }

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => {
        visible = entries.some(entry => entry.isIntersecting && entry.intersectionRatio > 0);
        synchronize();
      }, { threshold: [0, 0.15] });
      observer.observe(root);
      disposers.push(() => observer.disconnect());
    } else {
      const checkVisibility = () => {
        const bounds = root.getBoundingClientRect();
        visible = bounds.bottom > 0 && bounds.top < window.innerHeight && bounds.right > 0 && bounds.left < window.innerWidth;
        synchronize();
      };
      listen(window, 'scroll', checkVisibility, { passive: true });
      listen(window, 'resize', checkVisibility);
      checkVisibility();
    }
    toggle.hidden = panels.length === 1 && !video();
    if (playlist) playlist.hidden = false;
    root.carbenOffersController = {
      destroy() {
        destroyed = true;
        pauseImage();
        panels.forEach(panel => pauseVideo(panel.querySelector('video')));
        disposers.forEach(dispose => dispose());
        delete root.carbenOffersController;
      },
    };
    updateControls();
  }

  const initializeWithin = context => {
    if (context.matches?.(selector)) initialize(context);
    context.querySelectorAll(selector).forEach(initialize);
  };
  initializeWithin(document);
  document.addEventListener('shopify:section:load', event => initializeWithin(event.target));
  document.addEventListener('shopify:section:unload', event => {
    if (event.target.matches?.(selector)) event.target.carbenOffersController?.destroy();
    event.target.querySelectorAll(selector).forEach(root => root.carbenOffersController?.destroy());
  });
})();
